import { ArrowRight2 } from "iconsax-reactjs";
import { AlertTriangle, X } from "lucide-react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";
import { MemberOption } from "./MemberSelectionDropdown";
import MemberSelectionDropdown from "./MemberSelectionDropdown";

type DeleteMemberModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberName: string;
  managedCustomersCount: number;
  remainingCustomersCount: number;
  canDelete: boolean;
  selectedMemberId: string;
  memberOptions: MemberOption[];
  memberQuery: string;
  isMemberDropdownOpen: boolean;
  onMemberDropdownOpenChange: (open: boolean) => void;
  onMemberQueryChange: (value: string) => void;
  onSelectReassignmentMember: (memberId: string) => void;
  onViewCustomers: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isDeleting: boolean;
  error?: string | null;
};

const DeleteMemberModal = ({
  open,
  onOpenChange,
  memberName,
  managedCustomersCount,
  remainingCustomersCount,
  canDelete,
  selectedMemberId,
  memberOptions,
  memberQuery,
  isMemberDropdownOpen,
  onMemberDropdownOpenChange,
  onMemberQueryChange,
  onSelectReassignmentMember,
  onViewCustomers,
  onCancel,
  onDelete,
  isDeleting,
  error,
}: DeleteMemberModalProps) => {
  const hasCustomers = managedCustomersCount > 0;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      contentClassName="max-w-[600px]"
    >
      <ModalHeader className="relative justify-center">
        <ModalTitle className="text-center">Remove {memberName}?</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          className="absolute right-6 top-6 rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF]"
          onClick={onCancel}
          disabled={isDeleting}
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="space-y-6 pt-4">
        <p className="text-center text-[16px] font-[400] leading-8 text-[#3A3E5B]">
          Removing {memberName} will remove their access to Tradevu Admin
          immediately.
          {hasCustomers
            ? " Reassign their customers before continuing."
            : " This can't be undone unless you re-invite them."}
        </p>

        {hasCustomers ? (
          <>
            <div className="flex items-center gap-3 rounded-[16px] bg-[#F7F2E3] px-5 py-4 text-[16px] text-[#161827]">
              <AlertTriangle size={24} className="shrink-0 text-[#D97706]" />
              <span>
                This member manages{" "}
                <span className="font-[600] text-[#B45309]">
                  {remainingCustomersCount.toLocaleString()} customers
                </span>
              </span>
            </div>

            <div>
              <div className="mb-2 text-[18px] font-[500] text-[#3A3E5B]">
                Reassign customers to
              </div>
              <MemberSelectionDropdown
                open={isMemberDropdownOpen}
                onOpenChange={onMemberDropdownOpenChange}
                members={memberOptions}
                selectedMemberId={selectedMemberId}
                onSelectMember={onSelectReassignmentMember}
                query={memberQuery}
                onQueryChange={onMemberQueryChange}
                placeholder="Select member"
              />
            </div>
          </>
        ) : null}
      </ModalBody>

      <ModalFooter className="justify-between gap-6">
        {hasCustomers ? (
          <button
            type="button"
            className="inline-flex h-11 items-center gap-2 text-[20px] font-[500] text-[#161827]"
            onClick={onViewCustomers}
            disabled={isDeleting}
          >
            View customers
            <ArrowRight2 size={20} className="text-[#3A3E5B]" />
          </button>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-6">
          <Button
            variant="ghost"
            className="h-11 rounded-[14px] px-8 text-[16px] font-[500] text-[#3A3E5B]"
            onClick={onCancel}
            disabled={isDeleting}
          >
            {hasCustomers ? "Go back" : "Cancel"}
          </Button>
          <Button
            className="h-11 rounded-[14px] bg-[#FF5757] px-8 text-[16px] font-[500] text-white hover:bg-[#E94848]"
            onClick={onDelete}
            disabled={!canDelete || isDeleting}
          >
            {isDeleting ? "Removing..." : "Remove member"}
          </Button>
        </div>
      </ModalFooter>
      {error ? <div className="px-6 pb-6 text-[14px] text-[#C24E4E]">{error}</div> : null}
    </Modal>
  );
};

export default DeleteMemberModal;
